import { NodeModel, NodeModelGenerics } from "@projectstorm/react-diagrams";
import { BaseModelOptions, GenerateModelEvent } from '@projectstorm/react-canvas-core';
import { MetricsPortModel } from "./MetricsPortModel";
import { getHashColor } from "./metrics_util";
import TitleService from "title-service";
import { GDS_BREAK } from "./util";

export interface DeviceNodeModelGenerics {
    PORT: MetricsPortModel
}
export const MetricsPorts = {
    Select: 'Select',
    Function: 'Function',
    RawMetric: 'RawMetric',
    MetricKeys: 'MetricKeys',
    Add: 'Add',
    HashAdd: 'HashAdd'
}
export class MetricsNodeModel extends NodeModel<NodeModelGenerics & DeviceNodeModelGenerics> {
    properties?: MetricsNodeModelOptions;
    color?: string;
    name?: string;
    memberPorts: MetricsPortModel[];
    inPorts: MetricsPortModel[];
    outPorts: MetricsPortModel[];
    constructor(options: MetricsNodeModelOptions = {}) {
        super({
            type: 'redquick-model',
            ...options,
        });
        this.memberPorts = [];
        this.outPorts = [];
        this.inPorts = [];
        this.name = '';
        this.properties = options;
        this.color = options.color || '#ff0000';
        if (this.properties.properties) {
            this.color = getHashColor(this.getNodeType());
            if (!_cloning)
                switch (this.getNodeType()) {
                    case 'RawMetricConfig':
                        this.configureRawMetricConfig(this.properties);
                        break;
                    case 'RawMetricProperties':
                        this.configureRawMetricProperties(this.properties);
                        break;
                    case 'OneOfOperation':
                        this.configureOneOfOperation(this.properties);
                        break;
                    case 'EqualOperation':
                        this.configureEqualOperation(this.properties);
                        break;
                    case 'AddFunction':
                        this.configureAddFunction(this.properties);
                        break;
                    case 'HashAddFunction':
                        this.configureHashAddFunction(this.properties);
                        break;
                    case 'NumberValue':
                        this.configureNumberValue(this.properties);
                        break;
                    case 'Browser':
                    case 'OperatingSystem':
                    case 'StringEnumeration':
                        this.configureStringEnumeration(this.properties);
                        break;
                    case 'ClassProperties':
                        this.configureClassProperties(this.properties);
                        break;
                }
        }
        this.registerListener({
            portUpdate: () => {
                let models: any = (this.getParent());
                if (models && models.parent) {
                    models.parent.fireEvent({}, 'nodeUpdate');
                }
            }
        });
    }
    isRawMetricConfig() {
        return this.getNodeType() === 'RawMetricConfig';
    }
    getPortByName(name: string, op?: GDS_BREAK, target_port?: string) {
        let group = [...this.inPorts, ...this.outPorts];
        switch (op) {
            case GDS_BREAK.IN:
                group = this.inPorts;
                break;
            case GDS_BREAK.OUT:
                group = this.outPorts;
                break;
        }
        if (target_port) {
            let foundOtherPort;
            let found = group.find(v => {
                let otherPorts = v.getOtherPorts();
                for (let i = 0; i < otherPorts.length; i++) {
                    let otherPort = otherPorts[i];
                    if (otherPort && otherPort.properties.propertyName === target_port) {
                        foundOtherPort = otherPort;
                        return v.properties.propertyName === name
                    }
                }
            });
            if (found) {
                return foundOtherPort;
            }
            else {
                return null;
            }
        }
        return group.find(v => v.properties.propertyName === name);
    }
    configureNumberValue(arg0: MetricsNodeModelOptions) {
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: true,
            name: arg0.properties.properties[0].name,
            propertyName: arg0.properties.properties[0].name,
            propertyType: arg0.properties.properties[0].name,
            connectableTypes: ['NUMBER']
        })));
    }
    configureAddFunction(arg0: MetricsNodeModelOptions) {
        this.name = 'Add';
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: true,
            name: 'Add',
            propertyName: 'Add',
            propertyType: 'Add',
            connectableTypes: ['Function']
        })));
    }
    configureHashAddFunction(arg0: MetricsNodeModelOptions) {
        this.name = 'HashAdd';
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: true,
            name: 'HashAdd',
            propertyName: 'HashAdd',
            propertyType: 'HashAdd',
            connectableTypes: ['Function']
        })));
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'Modulo',
            propertyName: 'Modulo',
            propertyType: 'Modulo',
            connectableTypes: ['NUMBER']
        })));
    }
    configureRawMetricConfig(arg0: MetricsNodeModelOptions) {
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'Select',
            propertyName: 'Select',
            propertyType: 'Select',
            connectableTypes: ['RawMetric']
        })));
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'Function',
            propertyName: 'Function',
            propertyType: 'Function',
            connectableTypes: ['Function']
        })));
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'MetricKeys',
            propertyName: 'MetricKeys',
            propertyType: 'MetricKeys',
            connectableTypes: ['STRING']
        })));
    }
    configureStringEnumeration(arg0: MetricsNodeModelOptions) {
        let { properties } = arg0.properties;
        if (properties) {
            properties.forEach((property) => {
                this.memberPorts.push(this.addPort(new MetricsPortModel({
                    in: true,
                    name: property.name,
                    propertyName: property.name,
                    propertyType: property.type,
                    connectableTypes: ['STRING']
                })));
            })
        }
    }
    configureClassProperties(arg0: MetricsNodeModelOptions) {
        let { properties, name } = arg0.properties;
        if (properties) {
            this.name = name;
            properties.forEach((property) => {
                this.memberPorts.push(this.addPort(new MetricsPortModel({
                    in: true,
                    name: property.name,
                    propertyName: property.name,
                    propertyType: property.type,
                    connectableTypes: ['STRING']
                })));
            })
        }
    }
    configureEqualOperation(properties: MetricsNodeModelOptions) {
        this.name = TitleService('=')
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: true,
            name: 'A',
            propertyName: 'A',
            propertyType: 'A',
            connectableTypes: ['STRING', 'FLOAT', 'BOOLEAN']
        })));
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'B',
            propertyName: 'B',
            propertyType: 'B',
            connectableTypes: ['STRING', 'FLOAT', 'BOOLEAN']
        })));
    }
    configureOneOfOperation(properties: MetricsNodeModelOptions) {
        this.name = TitleService('One of')
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: true,
            name: 'One of',
            propertyName: 'One of',
            propertyType: 'One of',
            connectableTypes: ['STRING', 'FLOAT', 'BOOLEAN']
        })));
        this.memberPorts.push(this.addPort(new MetricsPortModel({
            in: false,
            name: 'these',
            propertyName: 'these',
            propertyType: 'these',
            connectableTypes: ['STRING', 'FLOAT', 'BOOLEAN']
        })));
    }
    configureRawMetricProperties(arg0: MetricsNodeModelOptions) {
        this.name = TitleService('Raw Metrics Properties')
        if (arg0.properties) {
            let { properties } = arg0.properties;
            if (properties) {
                properties.forEach(({ defaultName, name, type }) => {
                    this.memberPorts.push(this.addPort(new MetricsPortModel({
                        in: false,
                        name: name,
                        propertyName: name,
                        propertyType: type,
                        connectableTypes: [type]
                    })));
                })
            }
            this.memberPorts.push(this.addPort(new MetricsPortModel({
                in: true,
                name: 'Raw Metric',
                propertyName: 'RawMetric',
                propertyType: 'RawMetric',
                connectableTypes: ['RawMetric']
            })));
        }
    }
    selectionChanged() {
        let models: any = (this.getParent());
        if (models && models.parent) {
            models.parent.fireEvent({}, 'nodeSelectionChanged');
        }
    }
    getColor() {
        return this.color;
    }
    getName() {
        return this.name;
    }
    isMemberPort(port: MetricsPortModel) {
        return this.memberPorts.indexOf(port) !== -1
    }
    getMemberPorts() {
        return this.memberPorts;
    }
    getInPorts() {
        return this.inPorts;
    }
    getOutPorts() {
        return this.outPorts;
    }
    synchronizePorts() {
    }

    cloning(on: boolean) {
        _cloning = on;
    }
    getNodeType() {
        if (!_cloning)
            if (this.properties && this.properties.properties) {
                return this.properties.properties.type;
            }
        return '';
    }
    addPort<T extends MetricsPortModel>(port: T): T {
        super.addPort(port);
        if (port.getOptions().in) {
            port.properties.singleLink = true;
            port.setMaximumLinks(1);
            this.inPorts.push(port);
        }
        else {
            this.outPorts.push(port);
        }
        return port;
    }

    serialize() {
        return {
            ...super.serialize(),
            properties: this.properties,
            color: this.color,
            name: this.name,
            memberPorts: this.memberPorts.map((port) => {
                return port.getID();
            }),
            inPorts: this.inPorts.map((port) => {
                return port.getID();
            }),
            outPorts: this.outPorts.map((port) => {
                return port.getID();
            })
        };
    }

    deserialize(event: any): void {
        super.deserialize(event);
        this.color = event.data.color;
        this.name = event.data.name;
        this.properties = event.data.properties;

        this.memberPorts = (event.data.memberPorts || []).map((id: string, index: number) => {
            let port = this.getPortFromID(id) as MetricsPortModel;


            return port;
        });
        this.inPorts = (event.data.inPorts || []).map((id: string) => {
            let port = this.getPortFromID(id) as MetricsPortModel;

            return port;
        });
        this.outPorts = (event.data.outPorts || []).map((id: string) => {
            return this.getPortFromID(id) as MetricsPortModel;
        });
    }
}
export interface MetricsNodeModelOptions extends BaseModelOptions {
    collapsed?: boolean;
    color?: string;
    properties?: {
        type: string,
        name?: string,
        properties: {
            type: string;
            name: string;
            defaultName: string;
        }[]
    }
}
let _cloning = false;
export function setCloning(val: boolean) {
    _cloning = val;
}